import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  CreditCard, Search, Star, Ticket, Gift, CheckCircle, XCircle,
  AlertCircle, AlertTriangle, TrendingUp, Clock, Users, Zap, Wallet,
  ChevronRight, ArrowRight, Loader2, ShoppingCart, Printer, MapPin,
  Smartphone, Link2, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import Talao from '../Talao';
import { mapearConsultaParaTalao } from '../talao_mapper';

const ConsultaOperacional = ({ user, initialClientId, embedded = false }) => {
  // === Estado: Pesquisa ===
  const [pesquisa, setPesquisa] = useState('');
  const [resultadosPesquisa, setResultadosPesquisa] = useState([]);
  const [pesquisando, setPesquisando] = useState(false);
  const [showResultados, setShowResultados] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // === Estado: Cliente identificado ===
  const [clienteData, setClienteData] = useState(null);
  const [loadingCliente, setLoadingCliente] = useState(false);

  // === Estado: Ações ===
  const [showUsarPontos, setShowUsarPontos] = useState(false);
  const [showUsarVale, setShowUsarVale] = useState(false);
  const [valeSelecionado, setValeSelecionado] = useState(null);
  const [pontosUsar, setPontosUsar] = useState('');
  const [valorCompra, setValorCompra] = useState('');
  const [numeroFatura, setNumeroFatura] = useState('');
  const [locais, setLocais] = useState([]);
  const [localSelecionado, setLocalSelecionado] = useState('');
  const locaisResgate = locais.filter(l => /Loja\s*Avelal|Loja\s*S[áa]t[ãa]o/i.test(l));
  const [processando, setProcessando] = useState(false);
  const [registoResultado, setRegistoResultado] = useState(null);
  const [showAssociarRFID, setShowAssociarRFID] = useState(false);
  const [novoRFID, setNovoRFID] = useState('');
  const [showUsarCashback, setShowUsarCashback] = useState(false);
  const [cashbackUsar, setCashbackUsar] = useState('');
  // 🆕 16-05-2026: Talão de benefícios
  const [showTalao, setShowTalao] = useState(false);
  const [talaoAutoPrint, setTalaoAutoPrint] = useState(false);

  useEffect(() => {
    api.get('/parcerias/locais').then(r => {
      setLocais([...new Set(r.data.locais || [])]);
      if (r.data.local_sugerido) setLocalSelecionado(r.data.local_sugerido);
    }).catch(() => {});
  }, []);

  // 🆕 Após compra bem-sucedida, abrir talão com auto-print
  const dispararTalaoPosCompra = useCallback(() => {
    setTalaoAutoPrint(true);
    setShowTalao(true);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && !inputRef.current?.contains(e.target)) {
        setShowResultados(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // === Pesquisa com debounce ===
  const pesquisarCliente = useCallback(async (q) => {
    if (q.length < 2) { setResultadosPesquisa([]); setShowResultados(false); return; }
    setPesquisando(true);
    try {
      const res = await api.get(`/parcerias/consulta-rapida/pesquisar?q=${encodeURIComponent(q)}`);
      setResultadosPesquisa(res.data || []);
      setShowResultados(true);
    } catch {
      setResultadosPesquisa([]);
    } finally {
      setPesquisando(false);
    }
  }, []);

  const handlePesquisaChange = (val) => {
    setPesquisa(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => pesquisarCliente(val), 300);
  };

  // Leitura directa por RFID (Enter no input)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && pesquisa.trim().length >= 2) {
      e.preventDefault();
      setShowResultados(false);
      carregarConsultaCompleta(pesquisa.trim());
    }
  };

  // === Carregar consulta operacional completa ===
  const carregarConsultaCompleta = useCallback(async (idCartao) => {
    setLoadingCliente(true);
    setClienteData(null);
    setRegistoResultado(null);
    try {
      const res = await api.get(`/parcerias/consulta-rapida/${encodeURIComponent(idCartao)}`);
      setClienteData(res.data);
      setShowResultados(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cliente nao encontrado');
    } finally {
      setLoadingCliente(false);
    }
  }, []);

  // Pré-carregar cliente quando o componente recebe initialClientId
  useEffect(() => {
    if (initialClientId) {
      carregarConsultaCompleta(initialClientId);
    }
  }, [initialClientId, carregarConsultaCompleta]);

  const selecionarDoResultado = (c) => {
    const id = c.id_cartao_rfid || c.id || c.nif || c.xd_data?.cartao_cliente || '';
    setPesquisa(c.nome || id);
    setShowResultados(false);
    if (id) carregarConsultaCompleta(id);
    else toast.error('Cliente sem identificador válido');
  };

  // === Usar Pontos ===
  const handleUsarPontos = async () => {
    const pts = parseInt(pontosUsar) || clienteData.pontos.saldo;
    if (pts <= 0 || pts > clienteData.pontos.saldo) { toast.error('Pontos invalidos'); return; }
    setProcessando(true);
    try {
      const res = await api.post('/parcerias/transacao-completa', {
        id_cartao_rfid: clienteData.cliente.id_cartao_rfid || clienteData.cliente.id,
        numero_fatura: numeroFatura.trim() || '',
        valor_compra: parseFloat(valorCompra) || 0,
        vouchers_usar: [],
        resgatar_pontos: true,
        pontos_resgatar: pts,
        modo_resgate: 'desconto',
        resgatar_cashback: false,
        local_compra: localSelecionado || null,
        tipo_compra: 'normal',
      });
      setRegistoResultado({ tipo: 'pontos', dados: res.data, pontos_usados: pts });
      setShowUsarPontos(false);
      toast.success('Pontos resgatados com sucesso');
      // 🆕 Refrescar clienteData
      const idLookup = clienteData.cliente.id_cartao_rfid || clienteData.cliente.id;
      if (idLookup) {
        try {
          const fresh = await api.get(`/parcerias/consulta-rapida/${encodeURIComponent(idLookup)}`);
          setClienteData(fresh.data);
          dispararTalaoPosCompra();
        } catch { /* ignore */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao resgatar pontos');
    } finally {
      setProcessando(false);
    }
  };

  // === Usar Cashback ===
  const handleUsarCashback = async () => {
    const val = parseFloat(cashbackUsar) || clienteData.cashback.saldo;
    if (val <= 0 || val > clienteData.cashback.saldo) { toast.error('Valor de cashback invalido'); return; }
    setProcessando(true);
    try {
      const res = await api.post('/parcerias/transacao-completa', {
        id_cartao_rfid: clienteData.cliente.id_cartao_rfid || clienteData.cliente.id,
        resgatar_cashback: true,
        cashback_valor: val,
        local_compra: localSelecionado || null,
      });
      // 🆕 Usar o VALOR REAL devolvido pelo backend (não o do input)
      const valorResgatado = parseFloat(res.data?.cashback_resgatado || 0);
      if (valorResgatado <= 0) {
        toast.error('Não foi possível resgatar o cashback. Saldo pode estar desactualizado — recarregue o cliente.');
      } else {
        setRegistoResultado({ tipo: 'cashback', dados: res.data, cashback_usado: valorResgatado });
        setShowUsarCashback(false);
        toast.success(`Cashback de ${valorResgatado.toFixed(2)} EUR resgatado`);
      }
      // 🆕 Refrescar clienteData para o saldo actualizar imediatamente na UI
      const idLookup = clienteData.cliente.id_cartao_rfid || clienteData.cliente.id;
      if (idLookup) {
        try {
          const fresh = await api.get(`/parcerias/consulta-rapida/${encodeURIComponent(idLookup)}`);
          setClienteData(fresh.data);
        } catch { /* ignore */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao resgatar cashback');
    } finally {
      setProcessando(false);
    }
  };

  // === Usar Vale ===
  const handleUsarVale = async () => {
    if (!valeSelecionado) return;
    if (!valorCompra || parseFloat(valorCompra) <= 0) { toast.error('Introduza o valor da compra'); return; }
    if (!numeroFatura.trim()) { toast.error('Introduza o numero da fatura'); return; }
    setProcessando(true);
    try {
      const res = await api.post('/parcerias/transacao-completa', {
        id_cartao_rfid: clienteData.cliente.id_cartao_rfid || clienteData.cliente.id,
        numero_fatura: numeroFatura.trim(),
        valor_compra: parseFloat(valorCompra),
        vouchers_usar: [valeSelecionado.id],
        resgatar_pontos: false,
        pontos_resgatar: null,
        modo_resgate: 'desconto',
        resgatar_cashback: false,
        local_compra: localSelecionado || null,
        tipo_compra: 'normal',
      });
      setRegistoResultado({ tipo: 'vale', dados: res.data, vale: valeSelecionado });
      setShowUsarVale(false);
      toast.success('Vale utilizado com sucesso');
      // 🆕 Refrescar clienteData
      const idLookup = clienteData.cliente.id_cartao_rfid || clienteData.cliente.id;
      if (idLookup) {
        try {
          const fresh = await api.get(`/parcerias/consulta-rapida/${encodeURIComponent(idLookup)}`);
          setClienteData(fresh.data);
          dispararTalaoPosCompra();
        } catch { /* ignore */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao utilizar vale');
    } finally {
      setProcessando(false);
    }
  };

  // === Reset ===
  const handleNovaConsulta = () => {
    setClienteData(null);
    setRegistoResultado(null);
    setPesquisa('');
    setValorCompra('');
    setNumeroFatura('');
    setPontosUsar('');
    setValeSelecionado(null);
    setNovoRFID('');
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // === Associar RFID a cliente app ===
  const handleAssociarRFID = async () => {
    if (!novoRFID.trim()) { toast.error('Introduza o ID do cartao RFID'); return; }
    setProcessando(true);
    try {
      await api.post(`/parcerias/clientes-rfid/${clienteData.cliente.id}/associar-rfid`, {
        id_cartao_rfid: novoRFID.trim(),
      });
      toast.success('Cartao RFID associado com sucesso');
      setShowAssociarRFID(false);
      setNovoRFID('');
      carregarConsultaCompleta(novoRFID.trim());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao associar cartao');
    } finally {
      setProcessando(false);
    }
  };

  // === RENDER ===
  const d = clienteData;
  const valesJaneira = d?.vales_ativos?.filter(v => v.usavel_janeira) || [];
  const valesParceiro = d?.vales_ativos?.filter(v => !v.usavel_janeira) || [];
  const temBeneficio = d && (
    d.pontos.pode_resgatar ||
    valesJaneira.length > 0 ||
    d.perfis_beneficio?.length > 0 ||
    d.cashback?.pode_resgatar
  );

  return (
    <div className="space-y-4" data-testid="consulta-operacional">
      {/* === BARRA DE PESQUISA (oculta em modo embedded) === */}
      {!embedded && (
      <div className="relative" data-testid="pesquisa-container">
        <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-gray-800 to-gray-700 rounded-xl shadow-lg">
          <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-amber-500/20 shrink-0">
            <CreditCard className="h-6 w-6 text-amber-400" />
          </div>
          <div className="flex-1 relative">
            <Input
              ref={inputRef}
              value={pesquisa}
              onChange={(e) => handlePesquisaChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ler cartao RFID ou pesquisar por nome, telefone, NIF..."
              className="h-12 text-base bg-white/10 border-white/20 text-foreground placeholder:text-foreground/50 pr-10 focus:bg-white/15 focus:border-amber-400"
              data-testid="input-pesquisa-cliente"
              autoFocus
            />
            {pesquisando && <Loader2 className="absolute right-3 top-3.5 h-5 w-5 text-foreground/50 animate-spin" />}
            {!pesquisando && pesquisa.length >= 2 && (
              <Search className="absolute right-3 top-3.5 h-5 w-5 text-foreground/40" />
            )}
          </div>
          {clienteData && (
            <Button onClick={handleNovaConsulta} variant="outline" size="sm" className="text-foreground border-white/30 hover:bg-white/10 shrink-0" data-testid="btn-nova-consulta">
              Nova Consulta
            </Button>
          )}
        </div>

        {/* Dropdown de resultados */}
        {showResultados && resultadosPesquisa.length > 0 && (
          <div ref={dropdownRef} className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border rounded-lg shadow-xl max-h-64 overflow-y-auto" data-testid="dropdown-resultados">
            {resultadosPesquisa.map(c => (
              <button
                key={c.id}
                onClick={() => selecionarDoResultado(c)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 border-b last:border-0 text-left transition-colors"
                data-testid={`resultado-${c.id}`}
              >
                <div>
                  <p className="font-medium text-sm flex items-center gap-1.5">
                    {c.nome || 'Sem nome'}
                    {c.tipo_registo === 'app' && <Badge className="bg-indigo-100 text-indigo-700 text-[10px] px-1 py-0 leading-tight"><Smartphone className="h-2.5 w-2.5 mr-0.5 inline" />App</Badge>}
                    {c.estado_cliente === 'pendente_app' && <Badge className="bg-orange-100 text-orange-700 text-[10px] px-1 py-0 leading-tight">Pendente</Badge>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {c.id_cartao_rfid && <span className="font-mono mr-3">{c.id_cartao_rfid}</span>}
                    {!c.id_cartao_rfid && c.tipo_registo === 'app' && <span className="mr-3 italic text-orange-600">Sem cartao RFID</span>}
                    {c.telefone && <span className="mr-3">{c.telefone}</span>}
                    {c.nif && <span>NIF: {c.nif}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="secondary" className="text-xs">{c.saldo_pontos} pts</Badge>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </button>
            ))}
          </div>
        )}
        {showResultados && !pesquisando && resultadosPesquisa.length === 0 && pesquisa.length >= 2 && (
          <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border rounded-lg shadow-xl p-4 text-center text-sm text-muted-foreground">
            Nenhum cliente encontrado
          </div>
        )}
      </div>
      )}

      {/* Loading */}
      {loadingCliente && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mr-3" />
          <span className="text-muted-foreground">A carregar dados do cliente...</span>
        </div>
      )}

      {/* === REGISTO RESULTADO === */}
      {registoResultado && (
        <Card className="border-2 border-green-400 bg-green-50/50" data-testid="registo-resultado">
          <CardContent className="pt-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-green-800 mb-2">
                  {registoResultado.tipo === 'pontos' ? 'Pontos Resgatados' : registoResultado.tipo === 'cashback' ? 'Cashback Resgatado' : 'Vale Utilizado'}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Cliente:</span> <strong>{registoResultado.dados.cliente}</strong></div>
                  <div><span className="text-muted-foreground">Fatura:</span> <strong>{registoResultado.dados.numero_fatura}</strong></div>
                  {registoResultado.tipo === 'pontos' && (
                    <>
                      <div><span className="text-muted-foreground">Pontos usados:</span> <strong>{registoResultado.dados.pontos_resgatados || registoResultado.pontos_usados}</strong></div>
                      <div><span className="text-muted-foreground">Desconto:</span> <strong className="text-green-700">{registoResultado.dados.valor_desconto_pontos?.toFixed(2)} EUR</strong></div>
                    </>
                  )}
                  {registoResultado.tipo === 'cashback' && (
                    <>
                      <div><span className="text-muted-foreground">Cashback usado:</span> <strong>{registoResultado.cashback_usado?.toFixed(2)} EUR</strong></div>
                      <div><span className="text-muted-foreground">Desconto:</span> <strong className="text-green-700">{registoResultado.dados.valor_desconto_cashback?.toFixed(2)} EUR</strong></div>
                    </>
                  )}
                  {registoResultado.tipo === 'vale' && registoResultado.dados.vouchers_validados?.length > 0 && (
                    <>
                      <div><span className="text-muted-foreground">Vale:</span> <strong>{registoResultado.vale.numero_vale}</strong></div>
                      <div><span className="text-muted-foreground">Desconto:</span> <strong className="text-green-700">{registoResultado.dados.vouchers_validados[0]?.valor?.toFixed(2)} EUR</strong></div>
                    </>
                  )}
                  <div><span className="text-muted-foreground">Saldo pontos final:</span> <strong>{registoResultado.dados.saldo_pontos_final}</strong></div>
                </div>
                <div className="mt-4 p-4 bg-red-50 border-2 border-red-400 rounded-xl animate-pulse" data-testid="instrucao-xd">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                      <AlertCircle className="h-7 w-7 text-red-600" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-red-800">
                        APLICAR DESCONTO NO XD
                      </p>
                      <p className="text-2xl font-black text-red-700 mt-1" data-testid="valor-desconto-xd">
                        {registoResultado.tipo === 'pontos'
                          ? `- ${registoResultado.dados.valor_desconto_pontos?.toFixed(2)} EUR`
                          : registoResultado.tipo === 'cashback'
                            ? `- ${registoResultado.dados.valor_desconto_cashback?.toFixed(2)} EUR`
                            : registoResultado.dados.vouchers_validados?.[0]
                              ? `- ${registoResultado.dados.vouchers_validados[0].valor?.toFixed(2)} EUR`
                              : 'Ver valor acima'}
                      </p>
                      <p className="text-xs text-red-600 mt-1">Aplicar este desconto manualmente no terminal XD antes de fechar a venda</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={() => { setRegistoResultado(null); carregarConsultaCompleta(d.cliente.id_cartao_rfid || d.cliente.id); }} data-testid="btn-atualizar-dados">
                    Atualizar Dados
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleNovaConsulta} data-testid="btn-novo-cliente">
                    Novo Cliente
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* === PAINEL CLIENTE === */}
      {d && !loadingCliente && (
      <>
      {d.arquivado && (
        <Card className="border-amber-400 bg-amber-50">
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 flex-1 min-w-[240px]">
              <AlertTriangle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-900 text-sm">Cliente Arquivado</p>
                <p className="text-xs text-amber-800 mt-0.5">
                  Este cliente foi arquivado{d.arquivado_por ? ` por ${d.arquivado_por}` : ''}.
                  Saldos e histórico mantêm-se. Reactiva para retomar pontos e cashback.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-500 text-amber-900 hover:bg-amber-100"
              data-testid="btn-reactivar-from-consulta"
              onClick={async () => {
                try {
                  const id = d.cliente.id || d.cliente.id_cartao_rfid;
                  await api.post(`/parcerias/clientes-rfid/${id}/reactivar`);
                  toast.success(`${d.cliente.nome} reactivado`);
                  window.location.reload();
                } catch (e) {
                  toast.error('Falha: ' + (e.response?.data?.detail || e.message));
                }
              }}
            >
              Reactivar agora
            </Button>
          </CardContent>
        </Card>
      )}
      <div className="space-y-4">
          {/* Header com dados + indicador de benefício */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Info cliente */}
            <Card className="lg:col-span-1" data-testid="card-info-cliente">
              <CardContent className="pt-5 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-400 text-lg">
                    {(d.cliente.nome || '?')[0]}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-base truncate" data-testid="cliente-nome">{d.cliente.nome || 'Sem nome'}</h3>
                    <p className="text-xs text-muted-foreground font-mono">{d.cliente.id_cartao_rfid || 'Sem cartao RFID'}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {d.cliente.tipo_registo === 'app' && <Badge className="bg-indigo-100 text-indigo-700 text-xs"><Smartphone className="h-3 w-3 mr-1" />App</Badge>}
                  {d.cliente.estado_cliente === 'pendente_app' && <Badge className="bg-orange-100 text-orange-700 text-xs">Pendente Validacao</Badge>}
                  {d.cliente.estado_cliente === 'ativo' && <Badge className="bg-green-100 text-green-700 text-xs"><CheckCircle className="h-3 w-3 mr-1" />Ativo</Badge>}
                  {d.cliente.nivel && <Badge variant="outline" className="text-xs">{d.cliente.nivel}</Badge>}
                </div>
                <div className="space-y-1 text-sm">
                  {d.cliente.telefone && <p className="text-muted-foreground">Tel: {d.cliente.telefone}</p>}
                  {d.cliente.nif && <p className="text-muted-foreground">NIF: {d.cliente.nif}</p>}
                </div>
                {/* Botão associar RFID para clientes app sem cartão */}
                {!d.cliente.id_cartao_rfid && (
                  <Button size="sm" variant="outline" className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={() => setShowAssociarRFID(true)} data-testid="btn-associar-rfid">
                    <Link2 className="h-3.5 w-3.5 mr-1.5" /> Associar Cartao RFID
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Benefícios disponíveis — destaque principal */}
            <Card className={`lg:col-span-2 border-2 ${temBeneficio ? 'border-green-400 bg-green-50/30' : 'border-gray-200'}`} data-testid="card-beneficios-resumo">
              <CardContent className="pt-5">
                <div className="flex items-center gap-2 mb-4">
                  {temBeneficio ? (
                    <Badge className="bg-green-600 text-foreground px-3 py-1 text-sm" data-testid="badge-beneficio-disponivel">
                      <CheckCircle className="h-4 w-4 mr-1.5" /> Beneficio disponivel
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="px-3 py-1 text-sm" data-testid="badge-sem-beneficio">
                      <XCircle className="h-4 w-4 mr-1.5" /> Sem beneficio aplicavel
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {/* Pontos */}
                  <div className={`rounded-lg p-3 text-center ${d.pontos.pode_resgatar ? 'bg-emerald-100 ring-2 ring-emerald-300' : 'bg-gray-100'}`} data-testid="bloco-pontos">
                    <Star className={`h-5 w-5 mx-auto mb-1 ${d.pontos.pode_resgatar ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                    <p className="text-2xl font-bold" data-testid="saldo-pontos">{d.pontos.saldo}</p>
                    <p className="text-xs text-muted-foreground">pontos</p>
                    <p className={`text-sm font-semibold mt-0.5 ${d.pontos.pode_resgatar ? 'text-emerald-700' : 'text-muted-foreground'}`} data-testid="equivalencia-euros">
                      = {(d.pontos.valor_euro || 0).toFixed(2)} EUR
                    </p>
                    {d.pontos.validade && (
                      <p className="text-[10px] text-muted-foreground mt-1" data-testid="validade-pontos">
                        Validade: {d.pontos.validade}
                      </p>
                    )}
                  </div>
                  
                  {/* Alerta Expiração de Pontos */}
                  {d.pontos.alerta_expiracao && (
                    <div className="col-span-2 sm:col-span-4 rounded-lg p-3 bg-red-50 border border-red-200 flex items-center gap-3" data-testid="alerta-expiracao-pontos">
                      <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-red-700">
                          {d.pontos.alerta_expiracao.pontos_a_expirar} pontos expiram em {d.pontos.alerta_expiracao.dias_restantes} dia(s)
                        </p>
                        <p className="text-xs text-red-500">
                          Validade: {d.pontos.alerta_expiracao.data_expiracao} · Valor: {d.pontos.alerta_expiracao.valor_euro?.toFixed(2)} EUR
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Vouchers */}
                  <div className={`rounded-lg p-3 text-center ${valesJaneira.length > 0 ? 'bg-amber-100 ring-2 ring-amber-300' : 'bg-gray-100'}`} data-testid="bloco-vouchers">
                    <Ticket className={`h-5 w-5 mx-auto mb-1 ${valesJaneira.length > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} />
                    <p className="text-2xl font-bold" data-testid="total-vales">{valesJaneira.length}</p>
                    <p className="text-xs text-muted-foreground">voucher(s)</p>
                    <p className={`text-sm font-semibold mt-0.5 ${valesJaneira.length > 0 ? 'text-amber-700' : 'text-muted-foreground'}`}>
                      {valesJaneira.reduce((s, v) => s + (v.valor || 0), 0).toFixed(2)} EUR
                    </p>
                  </div>
                  {/* Parcerias */}
                  <div className={`rounded-lg p-3 text-center ${d.perfis_beneficio?.length > 0 ? 'bg-blue-100 ring-2 ring-blue-300' : 'bg-gray-100'}`} data-testid="bloco-parcerias">
                    <Users className={`h-5 w-5 mx-auto mb-1 ${d.perfis_beneficio?.length > 0 ? 'text-blue-600' : 'text-muted-foreground'}`} />
                    <p className="text-2xl font-bold">{(d.perfis_beneficio?.length || 0) + (d.parcerias?.length || 0)}</p>
                    <p className="text-xs text-muted-foreground">parceria(s)</p>
                    <p className={`text-sm font-semibold mt-0.5 ${d.perfis_beneficio?.length > 0 ? 'text-blue-700' : 'text-muted-foreground'}`}>
                      {d.perfis_beneficio?.length > 0 ? 'Desconto direto' : 'Nenhuma'}
                    </p>
                  </div>
                  {/* Cashback */}
                  <div className={`rounded-lg p-3 text-center ${d.cashback?.pode_resgatar ? 'bg-purple-100 ring-2 ring-purple-300' : 'bg-gray-100'}`} data-testid="bloco-cashback">
                    <Wallet className={`h-5 w-5 mx-auto mb-1 ${d.cashback?.pode_resgatar ? 'text-purple-600' : 'text-muted-foreground'}`} />
                    <p className="text-2xl font-bold">{d.cashback?.saldo?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-muted-foreground">EUR cashback</p>
                    <p className={`text-sm font-semibold mt-0.5 ${d.cashback?.pode_resgatar ? 'text-purple-700' : 'text-amber-700'}`}>
                      {d.cashback?.pode_resgatar
                        ? 'Resgatavel'
                        : (d.cashback?.saldo > 0 ? 'Pendente' : 'Sem saldo')}
                    </p>
                    {d.cashback?.motivo_bloqueio && (
                      <p className="text-[10px] text-amber-700 mt-1 leading-tight" data-testid="cashback-motivo">
                        {d.cashback.motivo_bloqueio}
                      </p>
                    )}
                    {!d.cashback?.motivo_bloqueio && d.cashback?.expira_em && (
                      <p className="text-[10px] text-purple-700 mt-1" data-testid="cashback-expira">
                        Resgate até {d.cashback.expira_em}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* === CAMPANHAS ATIVAS === */}
          {d.campanhas_ativas?.length > 0 && (
            <Card className="border-2 border-amber-400 bg-amber-50" data-testid="card-campanhas-ativas">
              <CardContent className="p-4">
                {d.campanhas_ativas.map((camp, idx) => (
                  <div key={idx} className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Gift className="h-5 w-5 text-amber-600" />
                      <span className="font-bold text-amber-800 text-base">{camp.nome}</span>
                    </div>
                    {camp.descricao && <p className="text-sm text-amber-700">{camp.descricao}</p>}
                    {(camp.inicio_acum || camp.inicio_resgate) && (
                      <div className="flex flex-col gap-0.5 text-xs text-amber-800 bg-white/60 rounded p-2 border border-amber-200">
                        {camp.inicio_acum && camp.fim_acum && (
                          <span>📥 <strong>Ganha</strong> de {camp.inicio_acum} a {camp.fim_acum}{camp.minimo_compra > 0 ? ` em compras ≥ ${camp.minimo_compra}€` : ''}</span>
                        )}
                        {camp.inicio_resgate && camp.fim_resgate && (
                          <span>📤 <strong>Levanta</strong> de {camp.inicio_resgate} a {camp.fim_resgate}{camp.minimo_compra_resgatar > 0 ? ` em compras ≥ ${camp.minimo_compra_resgatar}€` : ''}</span>
                        )}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3 mt-1">
                      {camp.minimo_compra > 0 && (
                        <Badge variant="outline" className="bg-white border-amber-300 text-amber-800">
                          Compra min: {camp.minimo_compra.toFixed(2)}EUR
                        </Badge>
                      )}
                      {camp.desconto_manual_percentagem > 0 && (
                        <Badge className="bg-red-500 text-white text-sm px-3 py-1">
                          Desconto {camp.desconto_manual_percentagem}% (aplicar no XD)
                        </Badge>
                      )}
                      {camp.cashback_percentagem > 0 && (
                        <Badge className="bg-emerald-500 text-white text-sm px-3 py-1">
                          +{camp.cashback_percentagem}% cashback automatico
                        </Badge>
                      )}
                      {camp.cashback_valor_fixo > 0 && (
                        <Badge className="bg-emerald-500 text-white text-sm px-3 py-1">
                          +{camp.cashback_valor_fixo.toFixed(2)}EUR cashback automatico
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* === CICLO FIDELIZAÇÃO === */}
          {d.ciclo_fidelizacao && d.ciclo_fidelizacao.estado !== 'inativo' && (
            <Card className={`border-2 ${
              d.ciclo_fidelizacao.bonus_disponivel ? 'border-green-400 bg-green-50' :
              d.ciclo_fidelizacao.janela_ativa ? 'border-blue-400 bg-blue-50' :
              'border-gray-300 bg-gray-50'
            }`} data-testid="card-ciclo-fidelizacao">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="h-5 w-5 text-yellow-500" />
                  <span className="font-bold text-base">Ciclo Fidelizacao</span>
                  {d.ciclo_fidelizacao.ciclos_completos > 0 && (
                    <Badge variant="outline" className="text-xs">
                      {d.ciclo_fidelizacao.ciclos_completos}x completado
                    </Badge>
                  )}
                </div>

                {d.ciclo_fidelizacao.bonus_disponivel && (
                  <div className="rounded-lg p-3 bg-green-100 border border-green-300">
                    <p className="font-bold text-green-800 text-lg">
                      Bonus {d.ciclo_fidelizacao.bonus_disponivel.valor?.toFixed(2)}EUR disponivel!
                    </p>
                    <p className="text-sm text-green-700">
                      Compra min: {d.ciclo_fidelizacao.bonus_disponivel.minimo_compra?.toFixed(2)}EUR
                      {' · '}Expira: {d.ciclo_fidelizacao.bonus_disponivel.expira_em}
                      {' · '}{d.ciclo_fidelizacao.bonus_disponivel.horas_restantes}h restantes
                    </p>
                  </div>
                )}

                {d.ciclo_fidelizacao.janela_ativa && (
                  <div className="rounded-lg p-3 bg-blue-100 border border-blue-300">
                    <p className="font-bold text-blue-800">
                      Compra ≥{d.ciclo_fidelizacao.janela_ativa.minimo_compra?.toFixed(2)}EUR ativa novo bonus de {d.ciclo_fidelizacao.janela_ativa.bonus_valor?.toFixed(2)}EUR!
                    </p>
                    <p className="text-sm text-blue-700">
                      Janela até: {d.ciclo_fidelizacao.janela_ativa.fim}
                      {' · '}{d.ciclo_fidelizacao.janela_ativa.dias_restantes} dia(s) restantes
                    </p>
                  </div>
                )}

                {(d.ciclo_fidelizacao.estado === 'bonus_expirado' || d.ciclo_fidelizacao.estado === 'janela_expirada') && (
                  <p className="text-sm text-muted-foreground">{d.ciclo_fidelizacao.mensagem}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* === ACOES RAPIDAS === */}
          <Card data-testid="card-acoes">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="h-4 w-4" /> Acoes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button
                  disabled={!d.pontos.pode_resgatar}
                  onClick={() => setShowUsarPontos(true)}
                  className={d.pontos.pode_resgatar ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-200 text-slate-500 cursor-not-allowed hover:bg-slate-200'}
                  data-testid="btn-usar-pontos"
                  title={!d.pontos.pode_resgatar && d.pontos.saldo > 0 ? `Pontos insuficientes (mínimo: ${d.pontos.minimo_resgate} pts, atual: ${d.pontos.saldo})` : ''}
                >
                  <Star className="h-4 w-4 mr-2" />
                  Usar Pontos ({d.pontos.saldo} = {(d.pontos.valor_euro || 0).toFixed(2)} EUR)
                </Button>
                <Button
                  disabled={valesJaneira.length === 0}
                  variant={valesJaneira.length > 0 ? 'default' : 'secondary'}
                  onClick={() => {
                    if (valesJaneira.length === 1) { setValeSelecionado(valesJaneira[0]); setShowUsarVale(true); }
                    else setShowUsarVale(true);
                  }}
                  className={valesJaneira.length > 0 ? 'bg-amber-600 hover:bg-amber-700' : 'bg-slate-200 text-slate-500 cursor-not-allowed hover:bg-slate-200'}
                  data-testid="btn-usar-vale"
                >
                  <Ticket className="h-4 w-4 mr-2" />
                  Usar Voucher ({valesJaneira.length})
                </Button>
                <Button
                  disabled={!d.cashback?.pode_resgatar || (d.cashback?.saldo_resgatavel ?? 0) <= 0}
                  onClick={() => { setShowUsarCashback(true); setCashbackUsar(''); }}
                  className={d.cashback?.pode_resgatar && (d.cashback?.saldo_resgatavel ?? 0) > 0 ? 'bg-purple-600 hover:bg-purple-700' : 'bg-slate-200 text-slate-500 cursor-not-allowed hover:bg-slate-200'}
                  data-testid="btn-usar-cashback"
                  title={d.cashback?.motivo_bloqueio || ((d.cashback?.saldo_resgatavel ?? 0) <= 0 ? 'Sem saldo cashback' : '')}
                >
                  <Wallet className="h-4 w-4 mr-2" />
                  Usar Cashback ({(d.cashback?.saldo_resgatavel ?? d.cashback?.saldo ?? 0).toFixed(2)} EUR)
                </Button>
                <Button
                  variant="outline"
                  disabled={!d.perfis_beneficio?.length}
                  data-testid="btn-info-parceria"
                  onClick={() => toast.info(
                    d.perfis_beneficio?.map(p => `${p.nome}: ${p.regras?.map(r => `${r.tipo_beneficio} ${r.valor_beneficio}`).join(', ') || 'ver regras'}`).join(' | ') || 'Sem perfis'
                  )}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Parceria / Perfil ({d.perfis_beneficio?.length || 0})
                </Button>
                <Button variant="ghost" onClick={handleNovaConsulta} data-testid="btn-sem-beneficio">
                  Nao usar beneficio
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setTalaoAutoPrint(false); setShowTalao(true); }}
                  data-testid="btn-talao-cliente"
                  title="Imprimir talão de benefícios"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Talão
                </Button>
              </div>
              {!d.pontos.pode_resgatar && d.pontos.saldo > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Pontos insuficientes para resgate (minimo: {d.pontos.minimo_resgate} pts, atual: {d.pontos.saldo})
                </p>
              )}
            </CardContent>
          </Card>

          {/* === DETALHES: Vouchers + Parcerias + Regras === */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Vouchers detalhados */}
            {d.vales_ativos?.length > 0 && (
              <Card data-testid="card-vales-detalhe">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
                    <Ticket className="h-4 w-4" /> Vales Activos ({d.total_vales_ativos})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {d.vales_ativos.map((v) => (
                    <div key={v.id} className={`flex items-center justify-between p-2.5 rounded-lg border text-sm ${v.usavel_janeira ? 'bg-amber-50/50 border-amber-200' : 'bg-gray-50 border-gray-200'}`} data-testid={`vale-detalhe-${v.numero_vale}`}>
                      <div className="min-w-0">
                        <p className="font-mono text-xs truncate">{v.numero_vale}</p>
                        <p className="text-xs text-muted-foreground truncate">{v.parceria} {v.tipo_beneficio && `(${v.tipo_beneficio})`}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className={v.usavel_janeira ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-400'}>
                          {v.valor?.toFixed(2)} EUR
                        </Badge>
                        {v.dias_restantes <= 7 && (
                          <Badge variant="destructive" className="text-xs">{v.dias_restantes}d</Badge>
                        )}
                        {v.usavel_janeira ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">Janeira</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">Parceiro</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Parcerias e Perfis */}
            {(d.perfis_beneficio?.length > 0 || d.parcerias?.length > 0) && (
              <Card data-testid="card-parcerias-detalhe">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
                    <Gift className="h-4 w-4" /> Parcerias e Perfis
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {d.perfis_beneficio?.map(p => (
                    <div key={p.id} className="p-2.5 rounded-lg bg-blue-50 border border-blue-200 text-sm" data-testid={`perfil-${p.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-blue-800">{p.nome}</span>
                        <Badge className="bg-blue-100 text-blue-700 text-xs">Desconto Direto</Badge>
                      </div>
                      {p.regras?.map((r, i) => (
                        <p key={i} className="text-xs text-blue-600 mt-1">
                          {r.tipo_beneficio} {r.valor_beneficio}{r.tipo_beneficio === 'Percentagem' ? '%' : ' EUR'}
                          {r.valor_minimo_compra > 0 && ` (min. ${r.valor_minimo_compra} EUR)`}
                        </p>
                      ))}
                    </div>
                  ))}
                  {d.parcerias?.map(p => (
                    <div key={p.id} className="p-2.5 rounded-lg bg-gray-50 border border-gray-200 text-sm" data-testid={`parceria-${p.id}`}>
                      <span className="font-medium">{p.nome}</span>
                      {p.regras?.map((r, i) => (
                        <p key={i} className="text-xs text-muted-foreground mt-1">
                          {r.tipo_beneficio} {r.valor_beneficio}{r.tipo_beneficio === 'Percentagem' ? '%' : ' EUR'}
                          {r.valor_minimo_compra > 0 && ` (min. ${r.valor_minimo_compra} EUR)`}
                        </p>
                      ))}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Campanhas ativas */}
          {d.campanhas_pontos?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {d.campanhas_pontos.map((c, i) => (
                <Badge key={i} className="bg-yellow-100 text-yellow-800 text-xs py-1 px-2.5">
                  <TrendingUp className="h-3 w-3 mr-1" /> {c.nome} ({c.multiplicador}x) {c.fim && `ate ${c.fim}`}
                </Badge>
              ))}
            </div>
          )}

          {/* Últimos movimentos */}
          {d.ultimos_movimentos?.length > 0 && (
            <Card className="border-dashed" data-testid="card-ultimos-movimentos">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" /> Últimos Movimentos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-muted-foreground border-b">
                        <th className="text-left pb-2">Data</th>
                        <th className="text-left pb-2">Movimento</th>
                        <th className="text-left pb-2">Fatura</th>
                        <th className="text-right pb-2">Compra</th>
                        <th className="text-right pb-2">Variação</th>
                        <th className="text-left pb-2 hidden md:table-cell">Local</th>
                        <th className="text-left pb-2 hidden md:table-cell">Operador</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.ultimos_movimentos.map((m, i) => {
                        const isResgate = m.tipo === 'resgate' || m.tipo === 'desconto' || m.tipo === 'resgate_bonus' || m.tipo === 'uso_bonus';
                        const isExpirou = m.tipo === 'expirou';
                        const isReset = m.tipo === 'reset_admin';
                        const isAjuste = m.tipo === 'ajuste_admin';
                        const isCashback = m.categoria === 'cashback';
                        const labelMov = isReset
                          ? (isCashback ? 'Cashback zerado' : 'Pontos zerados')
                          : (isAjuste
                            ? (isCashback ? 'Ajuste admin (€)' : 'Ajuste admin (pts)')
                            : (isExpirou
                              ? (isCashback ? 'Cashback expirou' : 'Pontos expiraram')
                              : (isCashback
                                ? (isResgate ? 'Cashback usado' : 'Cashback ganho')
                                : (isResgate ? 'Pontos usados' : 'Pontos ganhos'))));
                        const corBadge = isReset
                          ? 'bg-slate-100 text-slate-700 border-slate-300'
                          : isAjuste
                            ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                            : isExpirou
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : (isCashback
                                ? (isResgate ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-purple-50 text-purple-700 border-purple-200')
                                : (isResgate ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'));
                        const valNum = isCashback ? (m.cashback || 0) : (m.pontos || 0);
                        const sinal = (isResgate || isExpirou || isReset) ? '−' : (valNum < 0 ? '−' : '+');
                        const unidade = isCashback ? 'EUR' : 'pts';
                        const formatado = isCashback ? Math.abs(valNum).toFixed(2) : Math.abs(valNum);
                        const variacao = `${sinal}${formatado} ${unidade}`;
                        const corVar = isReset
                          ? 'text-slate-600'
                          : isExpirou
                            ? 'text-amber-700'
                            : (isResgate ? 'text-rose-600' : (isCashback ? 'text-purple-700' : 'text-emerald-600'));
                        // 🆕 Resgate de cashback ligado a fatura real (match automático)
                        const matchFatura = isCashback && isResgate ? m.fatura_match : null;
                        const matchValor = isCashback && isResgate ? m.fatura_match_valor : null;
                        return (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-1.5 text-xs whitespace-nowrap">{m.data}</td>
                            <td className="py-1.5">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs ${corBadge}`}>
                                {labelMov}
                              </span>
                              {m.tipo === 'bonus_adesao' && m.validade && (
                                <span className="block text-[10px] text-amber-700 mt-0.5" data-testid="bonus-validade">
                                  Valido ate {m.validade} (min {(m.minimo_compra_uso || 25).toFixed?.(2) || '25.00'}EUR)
                                </span>
                              )}
                              {/* 🆕 Janela de resgate da campanha para cashback ganho de campanha */}
                              {isCashback && !isResgate && m.tipo === 'acumulacao' && (m.resgate_de || m.resgate_ate) && (
                                <span className="block text-[10px] text-purple-700 mt-0.5" data-testid="cashback-resgate-janela">
                                  {m.resgate_de && m.resgate_ate
                                    ? `Resgate de ${m.resgate_de} ate ${m.resgate_ate}`
                                    : (m.resgate_de
                                      ? `Resgate a partir de ${m.resgate_de}`
                                      : `Resgate ate ${m.resgate_ate}`)
                                  }
                                  {m.minimo_compra_resgatar > 0 ? ` (min ${Number(m.minimo_compra_resgatar).toFixed(2)}EUR)` : ''}
                                </span>
                              )}
                              {m.tipo === 'resgate_bonus' && (
                                <span className="block text-[10px] text-muted-foreground mt-0.5">
                                  Bonus de adesao consumido
                                </span>
                              )}
                            </td>
                            <td className="py-1.5 font-mono text-xs">
                              {(() => {
                                const fat = m.fatura || '';
                                const isResgateInternoSemMatch = isCashback && isResgate && fat.startsWith('RES-') && !matchFatura;
                                if (isResgateInternoSemMatch) return '—';
                                return fat || '—';
                              })()}
                              {matchFatura && (
                                <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-sans" title="Resgate aplicado nesta fatura">
                                  → {matchFatura}
                                </span>
                              )}
                            </td>
                            <td className="py-1.5 text-right text-xs">
                              {isResgate && isCashback ? (
                                matchFatura && matchValor ? (
                                  <span>
                                    <span className="font-medium">{matchValor.toFixed(2)} EUR</span>
                                    <span className="block text-[10px] text-muted-foreground">
                                      pago {Math.max(0, matchValor - Math.abs(m.cashback || 0)).toFixed(2)}€
                                    </span>
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground" title="Resgate antes da venda ser finalizada no XD">—</span>
                                )
                              ) : (m.valor_compra > 0) ? `${m.valor_compra.toFixed(2)} EUR` : '—'}
                            </td>
                            <td className={`py-1.5 text-right font-medium ${corVar}`}>{variacao}</td>
                            <td className="py-1.5 text-xs text-muted-foreground hidden md:table-cell">{m.local || '—'}</td>
                            <td className="py-1.5 text-xs text-muted-foreground hidden md:table-cell">{m.operador || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Total acumulado: {(d.total_acumulado || 0).toFixed(2)} EUR</p>
                <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 leading-snug">
                  💡 <strong>Cashback ganho</strong> mostra o valor da fatura que originou.{' '}
                  <strong>Cashback usado</strong> liga-se à fatura real após a venda no XD (auto-match).{' '}
                  Para os critérios das campanhas activas, ver <em>Em Vigor</em>.
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </>
      )}

      {/* Mensagem inicial quando não há pesquisa */}
      {!clienteData && !loadingCliente && !showResultados && (
        <div className="text-center py-16 text-muted-foreground" data-testid="placeholder-inicial">
          <CreditCard className="h-16 w-16 mx-auto mb-4 text-foreground" />
          <h3 className="text-lg font-medium text-muted-foreground mb-1">Consulta Operacional</h3>
          <p className="text-sm max-w-md mx-auto">
            Leia o cartao RFID ou pesquise pelo nome, telefone, NIF ou numero do cartao para consultar os beneficios do cliente.
          </p>
        </div>
      )}
      {!clienteData && !loadingCliente && !showResultados && (
        <div className="text-center py-16 text-muted-foreground" data-testid="placeholder-inicial">
          <CreditCard className="h-16 w-16 mx-auto mb-4 text-foreground" />
          <h3 className="text-lg font-medium text-muted-foreground mb-1">Consulta Operacional</h3>
          <p className="text-sm max-w-md mx-auto">
            Leia o cartao RFID ou pesquise pelo nome, telefone, NIF ou numero do cartao para consultar os beneficios do cliente.
          </p>
        </div>
      )}

      {/* === DIALOG: Usar Pontos === */}
      <Dialog open={showUsarPontos} onOpenChange={setShowUsarPontos}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-emerald-600" /> Resgatar Pontos
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm">
              <p>Saldo: <strong>{d?.pontos?.saldo} pontos</strong> = <strong>{d?.pontos?.valor_euro?.toFixed(2)} EUR</strong></p>
            </div>
            <div>
              <label className="text-sm font-medium">Pontos a resgatar</label>
              <Input type="number" value={pontosUsar} onChange={(e) => setPontosUsar(e.target.value)} placeholder={`Max: ${d?.pontos?.saldo}`} data-testid="input-pontos-resgatar" />
              {pontosUsar && (
                <p className="text-xs text-emerald-600 mt-1">
                  = {(parseInt(pontosUsar) * (d?.pontos?.valor_ponto || 0.01)).toFixed(2)} EUR de desconto
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Local</label>
              <select value={localSelecionado} onChange={(e) => setLocalSelecionado(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="select-local-pontos">
                <option value="">Selecionar...</option>
                {locaisResgate.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUsarPontos(false)} disabled={processando}>Cancelar</Button>
            <Button onClick={handleUsarPontos} disabled={processando} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-confirmar-pontos">
              {processando ? 'A processar...' : 'Confirmar Resgate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === DIALOG: Usar Cashback === */}
      <Dialog open={showUsarCashback} onOpenChange={setShowUsarCashback}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-purple-600" /> Resgatar Cashback
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm">
              <p>Saldo disponivel: <strong>{(d?.cashback?.saldo || 0).toFixed(2)} EUR</strong></p>
            </div>
            <div>
              <label className="text-sm font-medium">Valor a descontar (EUR)</label>
              <Input type="number" step="0.01" value={cashbackUsar} onChange={(e) => setCashbackUsar(e.target.value)} placeholder={`Max: ${(d?.cashback?.saldo || 0).toFixed(2)}`} data-testid="input-cashback-resgatar" />
            </div>
            <div>
              <label className="text-sm font-medium">Local</label>
              <select value={localSelecionado} onChange={(e) => setLocalSelecionado(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="select-local-cashback">
                <option value="">Selecionar...</option>
                {locaisResgate.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUsarCashback(false)} disabled={processando}>Cancelar</Button>
            <Button onClick={handleUsarCashback} disabled={processando} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-confirmar-cashback">
              {processando ? 'A processar...' : 'Confirmar Resgate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === DIALOG: Usar Vale === */}
      <Dialog open={showUsarVale} onOpenChange={(v) => { setShowUsarVale(v); if (!v) setValeSelecionado(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5 text-amber-600" /> Utilizar Voucher
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!valeSelecionado && valesJaneira.length > 1 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Selecione o vale a utilizar:</p>
                {valesJaneira.map(v => (
                  <button key={v.id} onClick={() => setValeSelecionado(v)} className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-colors hover:bg-amber-50 ${valeSelecionado?.id === v.id ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`} data-testid={`selecionar-vale-${v.numero_vale}`}>
                    <div>
                      <p className="font-mono text-sm">{v.numero_vale}</p>
                      <p className="text-xs text-muted-foreground">{v.parceria}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-amber-100 text-amber-700">{v.valor?.toFixed(2)} EUR</Badge>
                      {v.validade && <span className="text-xs text-muted-foreground">ate {v.validade}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {valeSelecionado && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                <p>Vale: <strong className="font-mono">{valeSelecionado.numero_vale}</strong></p>
                <p>Valor: <strong>{valeSelecionado.valor?.toFixed(2)} EUR</strong> ({valeSelecionado.parceria})</p>
                {valeSelecionado.validade && <p>Validade: {valeSelecionado.validade}</p>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Valor Compra (EUR) *</label>
                <Input type="number" step="0.01" value={valorCompra} onChange={(e) => setValorCompra(e.target.value)} placeholder="0.00" data-testid="input-valor-vale" />
              </div>
              <div>
                <label className="text-sm font-medium">N.o Fatura *</label>
                <Input value={numeroFatura} onChange={(e) => setNumeroFatura(e.target.value)} placeholder="FT 2026/..." data-testid="input-fatura-vale" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Local</label>
              <select value={localSelecionado} onChange={(e) => setLocalSelecionado(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="select-local-vale">
                <option value="">Selecionar...</option>
                {locaisResgate.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUsarVale(false); setValeSelecionado(null); }} disabled={processando}>Cancelar</Button>
            <Button onClick={handleUsarVale} disabled={processando || !valeSelecionado} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-confirmar-vale">
              {processando ? 'A processar...' : 'Confirmar Vale'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === DIALOG: Associar RFID === */}
      <Dialog open={showAssociarRFID} onOpenChange={setShowAssociarRFID}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-indigo-600" /> Associar Cartao RFID
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm">
              <p>Cliente: <strong>{d?.cliente?.nome}</strong></p>
              <p>Telefone: {d?.cliente?.telefone}</p>
              <p className="text-xs text-indigo-600 mt-1">Leia o cartao RFID ou introduza o ID manualmente</p>
            </div>
            <div>
              <label className="text-sm font-medium">ID Cartao RFID *</label>
              <Input
                value={novoRFID}
                onChange={(e) => setNovoRFID(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAssociarRFID(); }}
                placeholder="Ler cartao ou introduzir ID..."
                autoFocus
                data-testid="input-novo-rfid"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAssociarRFID(false); setNovoRFID(''); }} disabled={processando}>Cancelar</Button>
            <Button onClick={handleAssociarRFID} disabled={processando || !novoRFID.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-confirmar-rfid">
              {processando ? 'A processar...' : 'Associar Cartao'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Talão de benefícios — overlay imprimível */}
      {showTalao && clienteData && (
        <Talao
          dados={mapearConsultaParaTalao(clienteData)}
          autoPrint={talaoAutoPrint}
          onClose={() => { setShowTalao(false); setTalaoAutoPrint(false); }}
        />
      )}
    </div>
  );
};

export default ConsultaOperacional;
